<template>
  <div class="chat-spinner-holder">
    <b-spinner v-if="hasScrollLoading || hasChatLoading || isFetchingMention" class="absolute-loading" />

    <div v-if="hasNoChat" class="no-chat-image">
      <img src="@/assets/images/whatsapp-illustration-gray.svg" alt="no-chat" />
    </div>

    <div
      v-else
      @scroll="checkScroll"
      :class="{ modalPadding: !fromOmnichannelDesktop }"
      class="chat-container"
      ref="chatContainer"
    >
      <div class="expiration-wrapper">
        <transition name="slide-down">
          <div v-if="showExpiresAt" :style="getExpirationStyle(expires_at)" class="expiration-info">
            <Icon icon="fe:clock" style="font-size: 20px" />

            <div>A sala expira em: {{ getExpirationHours(expires_at) }}</div>
          </div>
        </transition>
      </div>

      <div v-for="(message, index) in allChatData" :key="message.id" class="message-divider-holder">
        <div
          v-if="isOpeningMessage(allChatData[index + 1], message)"
          @click="openOpportunity(message)"
          class="line-and-chat-start cursor-pointer"
        >
          <div class="start-message">
            {{ message.dialog.bot_name }} - Sala n° {{ message.dialog_id }} - Oportunidade:
            {{ message?.dialog?.opportunity?.id }} -
            {{ getFormattedDate(message.sent_at, true) }}
          </div>
        </div>

        <div
          v-else-if="isPreviousMessageFromDifferentDay(message, allChatData[index + 1])"
          class="line-and-chat-start"
        >
          <div class="start-message">
            {{ getFormattedDate(message.sent_at) }}
          </div>
        </div>

        <div :style="getHolderStyleDependingOnSender(message, allChatData[index - 1])" class="message-holder">
          <div v-if="message.is_deleted" class="deleted-message">
            <feather-icon icon="SlashIcon" />

            Mensagem deletada para todos!
          </div>

          <div
            v-else-if="getMimeType(message?.file?.mime) == 'audio'"
            :class="{ 'has-reaction': message.reaction_emoji }"
            :id="'message-card-' + message.id"
            class="audio-holder"
          >
            <IOSAudioPlayer v-if="isIOS()" :src="message.file.url" />

            <audio v-else @play="stopOtherAudios($event)" ref="audio-player" preload="auto" controls>
              <source :src="message.file.url" :type="message.file.mime" />
            </audio>

            <time-view-check
              :message="message"
              :style="`bottom: ${isIOS() ? '4' : '9'}px; ${
                message.user_id || message.bot_name ? 'right:' : 'left:'
              } 18px;`"
              class="absolute-time-check"
            />

            <div
              v-if="checkIfCanDeleteMessage(message)"
              @click="deleteMessage(message)"
              :class="{
                'desktop-message-actions': isIOS(),
                'desktop-message-actions-native': !isIOS(),
              }"
            >
              <feather-icon v-if="!hasDeletionRequest" icon="Trash2Icon" color="#c01220" />

              <b-spinner v-else small />
            </div>

            <div
              v-if="message.reaction_emoji"
              :style="message.user_id || message.bot_name ? 'left: 12px' : 'right: 12px'"
              class="audio-reaction"
            >
              {{ message.reaction_emoji }}
            </div>
          </div>

          <div
            v-else
            @touchstart="setMessageActionsTimer(message)"
            @touchend="removeMessageActionsTimer()"
            @touchmove="removeMessageActionsTimer()"
            :id="'message-card-' + message.id"
            :style="getMessageStyleDependingOnSender(message, allChatData[index - 1])"
            class="message-card"
          >
            <div
              v-if="message.referral"
              @click="openReferralSource(message.referral)"
              :class="{ 'referral-clickable': Boolean(message.referral.source_url) }"
              class="referral-card"
            >
              <div class="referral-label">
                <Icon icon="mdi:bullhorn-outline" />

                {{ getReferralLabel(message.referral) }}
              </div>

              <img
                v-if="message.referral.image_url"
                :src="message.referral.image_url"
                alt="referral-thumbnail"
                class="referral-thumbnail"
              />

              <div v-if="message.referral.headline" class="referral-headline">
                {{ message.referral.headline }}
              </div>

              <div v-if="message.referral.body" class="referral-body">
                {{ message.referral.body }}
              </div>

              <div v-if="message.referral.source_url" class="referral-link">
                <Icon icon="gridicons:external" />

                Ver origem
              </div>
            </div>

            <div
              v-if="message.parent"
              @click="goToMention('message-card-' + message.parent.id, message.parent.id, true)"
              class="response-message"
            >
              <div v-if="getMimeType(message.parent.file?.mime) == 'audio'" class="midia-response">
                <feather-icon icon="HeadphonesIcon" size="20" />

                Áudio
              </div>

              <div v-else-if="getMimeType(message.parent.file?.mime) == 'image'" class="midia-response">
                <feather-icon icon="ImageIcon" size="20" />

                Foto
              </div>

              <div v-else-if="getMimeType(message.parent.file?.mime) == 'video'" class="midia-response">
                <feather-icon icon="VideoIcon" size="20" />

                Vídeo
              </div>

              <div v-else-if="getMimeType(message.parent.file?.mime) == 'document'" class="midia-response">
                <feather-icon icon="FileTextIcon" size="20" />

                {{ message.parent.file.name }}
              </div>

              <div
                v-else-if="message.parent.message"
                v-dompurify-html="$service.formattedHtmlText(message.parent.message)"
              />

              <div v-else>-</div>
            </div>

            <div
              v-if="message.header"
              class="message-header"
              v-dompurify-html="$service.formattedHtmlText(message.header)"
            />

            <div v-if="message.file" class="file-holder">
              <img
                v-if="getMimeType(message.file?.mime) == 'image'"
                @click="openCarouselModal(message.file)"
                :id="'message-' + message.id"
                :class="{ 'file-error': message.error_message }"
                :style="{ maxWidth: mediaMaxWidth }"
                :src="message.file.url"
                :alt="message.file.name"
                class="visual-file"
              />

              <div
                v-else-if="getMimeType(message.file?.mime) == 'sticker'"
                :id="'message-' + message.id"
                class="sticker-file"
              >
                Sticker ainda não suportado!
              </div>

              <video
                v-else-if="getMimeType(message.file?.mime) == 'video'"
                :id="'message-' + message.id"
                :class="{ 'file-error': message.error_message }"
                :style="{ maxWidth: mediaMaxWidth }"
                class="visual-file"
                controls
              >
                <source :src="message.file.url" />
                Seu navegador não suporta a reprodução de vídeos.
              </video>

              <div
                v-else-if="
                  getMimeType(message.file?.mime) == 'document' && isPreviewableDocument(message.file)
                "
                :id="'message-' + message.id"
                :class="{ 'file-error': message.error_message }"
                :style="{ maxWidth: mediaMaxWidth }"
                class="document-preview-wrapper"
              >
                <div v-if="message.file.mime === 'application/pdf'" class="pdf-preview-card">
                  <object
                    :data="pdfViewerUrl(message.file)"
                    type="application/pdf"
                    class="pdf-object nice-Vscroll"
                  >
                    Prévia indisponível.
                  </object>

                  <div class="doc-meta-row">
                    <feather-icon icon="FileTextIcon" size="18" />

                    <span class="file-name" :title="message.file.name">
                      {{ message.file.name }}
                    </span>

                    <span @click="openDocumentInBlank(message.file)" class="preview-action">
                      <Icon icon="gridicons:external" />
                    </span>

                    <span @click.prevent="downloadFile(message.file)" class="preview-action">
                      <Icon icon="material-symbols:download" />
                    </span>
                  </div>
                </div>

                <div
                  v-else
                  class="text-preview-card"
                  :class="{ expanded: documentPreviewStatus[message.id]?.expanded }"
                >
                  <div class="text-preview-header">
                    <feather-icon icon="FileTextIcon" size="18" />

                    <span class="file-name" :title="message.file.name">
                      {{ message.file.name }}
                    </span>

                    <span @click="openDocumentInBlank(message.file)" class="preview-action">
                      <Icon icon="gridicons:external" />
                    </span>

                    <span @click.prevent="downloadFile(message.file)" class="preview-action">
                      <Icon icon="material-symbols:download" />
                    </span>

                    <span class="preview-action" @click="togglePreview(message)">
                      <Icon
                        :icon="
                          documentPreviewStatus[message.id]?.expanded
                            ? 'mdi:eye-off-outline'
                            : 'mdi:eye-outline'
                        "
                      />
                    </span>
                  </div>

                  <div
                    class="text-preview-body nice-Vscroll"
                    v-if="documentPreviewStatus[message.id]?.expanded"
                  >
                    <b-spinner small v-if="documentPreviewStatus[message.id]?.loading" />

                    <pre v-else-if="documentPreviewStatus[message.id]?.content" class="text-content">
                      {{ documentPreviewStatus[message.id].content }}
                    </pre>

                    <div v-else-if="documentPreviewStatus[message.id]?.error" class="preview-error">
                      Prévia não carregada: {{ documentPreviewStatus[message.id].error }}
                    </div>

                    <div v-else class="preview-hint">Carregando...</div>
                  </div>
                </div>
              </div>

              <a
                v-else-if="getMimeType(message.file?.mime) == 'document'"
                :id="'message-' + message.id"
                :class="{ 'file-error': message.error_message }"
                :href="message.file.url"
                class="document-file"
                target="_blank"
                rel="noopener"
              >
                <div class="file-name">{{ message.file.name }}</div>

                <i class="icon-arrow-down" />
              </a>
            </div>

            <div
              v-if="checkIfCanDeleteMessage(message)"
              @click="deleteMessage(message)"
              class="desktop-message-actions"
            >
              <feather-icon v-if="!hasDeletionRequest" icon="Trash2Icon" color="#c01220" />

              <b-spinner v-else small />
            </div>

            <div
              v-if="message.message"
              v-dompurify-html="$service.formattedHtmlTextMessage(message.message)"
            />

            <div
              v-if="message.footer"
              v-dompurify-html="$service.formattedHtmlText(message.footer)"
              class="message-footer"
            />

            <time-view-check :message="message" />

            <div v-if="message.reaction_emoji" class="message-reaction">
              {{ message.reaction_emoji }}
            </div>
          </div>

          <div
            v-if="message.error_message"
            :style="message.user_id || message.bot_name ? 'margin-right: 0.5rem' : ''"
            class="message-error-holder"
          >
            <div :id="'message-' + message.id">
              <feather-icon icon="AlertCircleIcon" color="#c01220" size="30" />
            </div>

            <b-tooltip
              v-if="message.error_message"
              :target="'message-' + message.id"
              triggers="hover"
              placement="left"
            >
              Erro: {{ message.error_message }}

              <span v-if="!message.file && !message.message">
                - O conteúdo da mensagem foi corrompido ou perdido e não pode ser reenviado
              </span>

              <span v-else-if="!isOpenRoom"> - Reabra a conversa para reenviar a mensagem </span>

              <span v-else-if="reengagementRequired"> - Envie um novo template para reengajar a conversa </span>
            </b-tooltip>

            <div
              v-if="(message.file || message.message) && isOpenRoom && !reengagementRequired"
              @click="resendMessage(message)"
              class="resend-holder"
            >
              <icon
                :id="`resend-icon-${message.id}`"
                :icon="
                  sendingMessageId === message.id ? 'svg-spinners:180-ring-with-bg' : 'ri:reset-right-line'
                "
                class="resend-icon"
              />

              <b-tooltip :target="`resend-icon-${message.id}`" triggers="hover" placement="left">
                Reenviar mensagem
              </b-tooltip>
            </div>
          </div>

          <avatar
            v-else-if="message.user_id"
            :class="{ invisible: isTheOtherMessageFromSameSender(message, chatData[index + 1]) }"
            :avatarLetter="$service.getFirstLetter(message.user?.name)"
            :size="2"
          />

          <img
            v-else-if="message.bot_name"
            :class="{ invisible: isTheOtherMessageFromSameSender(message, chatData[index + 1]) }"
            src="@/assets/images/icons/imbot-avatar.svg"
            alt="bot-icon"
            class="bot-avatar"
          />
        </div>
      </div>
    </div>

    <b-modal
      v-if="!fromOmniRedirect"
      id="bv-opportunity-from-chat-redirect"
      class="modal-dialog"
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
    >
      <opportunity-tabs
        v-if="opportunityFromRedirect"
        @saved="$emit('saved')"
        @close="$emit('closed')"
        @roomReopened="$emit('roomReopened', $event)"
        @opportunityRedirect="handleRedirectFromRedirect($event)"
        :opportunity_id="opportunityFromRedirect.id"
        :fromOmniRedirect="true"
      />
    </b-modal>
  </div>
</template>

<script>
import { BSpinner, BTooltip } from "bootstrap-vue";
import { Icon } from "@iconify/vue2";
import moment from "moment";

import Avatar from "@/views/components/Avatar.vue";
import IOSAudioPlayer from "@/views/components/chat/IOSAudioPlayer.vue";
import TimeViewCheck from "@/views/components/chat/TimeViewCheck.vue";

export default {
  name: "OmnichatComponent",

  components: {
    BSpinner,
    BTooltip,
    Icon,

    Avatar,
    IOSAudioPlayer,
    TimeViewCheck,

    OpportunityTabs: () => import("@/views/forms/OpportunityTabs.vue"),
  },

  props: {
    opportunity: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },

    fromOmnichannelDesktop: {
      type: Boolean,
      default: false,
      required: false,
    },

    fromOmniRedirect: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data() {
    return {
      newestChatId: null,
      currentPastChatId: null,

      mediaMaxWidth: null,
      resizeObserver: null,

      chatData: [],
      previousChatData: [],
      allPreviousChatData: [],

      messageActionsTimer: null,
      hasNewMessage: false,
      pullingLoop: null,
      loopBreak: false,

      newestOffset: 0,
      currentPastOffset: 0,
      limit: 20,

      firstTotalCount: 0,
      currentTotalCount: 0,
      currentIndexOfAllPreviousChats: 0,

      hasChatLoading: false,
      hasScrollLoading: false,
      hasPullingLoading: false,
      hasOpReselectRequest: false,
      hasDeletionRequest: false,

      hasNoChat: false,
      hasPreviousChat: false,
      hasReachedStartOfCurrentConversation: false,
      isFetchingMention: false,

      sendingMessageId: null,

      opportunityFromRedirect: null,

      showExpiresAt: false,
      expiresTimout: null,
      lastScrollTop: 0,

      documentPreviewStatus: {}, // { [messageId]: { loading, error, content, expanded } }
      previewableTextMimes: ["text/plain", "text/csv", "text/tsv", "text/html", "text/rtf"],
    };
  },

  computed: {
    allChatData() {
      return [...this.chatData, ...this.allPreviousChatData];
    },

    oppReselectData() {
      return this.$store.getters["omnichannel/getOpportunityReselectData"];
    },

    isOpenRoom() {
      if (!this.opportunity.dialogs[0]) return false;

      return (
        this.opportunity.dialogs[0].closed_at === undefined || this.opportunity.dialogs[0].closed_at === null
      );
    },

    currentDialog() {
      return this.allChatData[0]?.dialog;
    },

    expires_at() {
      return this.currentDialog?.expires_at;
    },

    media_type() {
      return this.currentDialog?.media_type;
    },

    reengagementRequired() {
      if (this.media_type !== "WhatsappWaba") return false;

      const alreadyExpired = this.expires_at && moment().isAfter(moment(this.expires_at));

      return !this.expires_at || alreadyExpired;
    },

    omnichannelSocketMessages() {
      return this.$store.state.omnichannelSocket.messages;
    },
  },

  mounted() {
    if ("ontouchstart" in window) document.addEventListener("contextmenu", (event) => event.preventDefault());

    this.getAllDialogs();

    this.setMediaWidthListener();
  },

  beforeDestroy() {
    this.loopBreak = true;

    this.stopPullingLoop();

    if (this.resizeObserver && this.$refs.chatContainer) {
      this.resizeObserver.unobserve(this.$refs.chatContainer);
    }

    document.removeEventListener("contextmenu", (event) => event.preventDefault());
  },

  watch: {
    "opportunity.dialogs": {
      handler() {
        this.getAllDialogs();
      },

      deep: true,
    },

    omnichannelSocketMessages: {
      handler() {
        const lastMessage = this.omnichannelSocketMessages[this.omnichannelSocketMessages.length - 1];

        if (!lastMessage) return;

        if (lastMessage.event === "MESSAGE_REACTION") {
          this.applyMessageReaction(lastMessage.data);
        }

        if (lastMessage.fromOmnichannel && lastMessage.resource.id == this.currentDialog?.id) {
          this.hasNewMessage = true;
        }
      },

      deep: true,
    },
  },

  methods: {
    downloadFile(file) {
      if (!file || !file.url) return;

      const filename = file.name || "arquivo";

      fetch(file.url, { mode: "cors" })
        .then((r) => r.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");

          a.href = blobUrl;
          a.download = filename;

          document.body.appendChild(a);

          a.click();
          a.remove();

          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        })
        .catch(() => {
          const a = document.createElement("a");

          a.href = file.url;
          a.setAttribute("download", filename);

          document.body.appendChild(a);

          a.click();
          a.remove();
        });
    },

    startPullingLoop() {
      if (this.hasNoChat || this.loopBreak) return;

      if (this.opportunity.dialogs[0]?.closed_at) return;

      if (this.pullingLoop) {
        clearInterval(this.pullingLoop);
      }

      this.pullingLoop = setInterval(() => {
        if (this.hasNewMessage) this.pullingFetch(this.newestChatId);
      }, 500);
    },

    stopPullingLoop() {
      clearInterval(this.pullingLoop);
    },

    setMediaWidthListener() {
      this.$nextTick(() => {
        const chatContainer = this.$refs.chatContainer;

        if (!chatContainer) return;

        let timeoutId = null;

        const debouncedUpdate = (width) => {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          timeoutId = setTimeout(() => {
            this.mediaMaxWidth = width - 75 + "px";
          }, 250);
        };

        this.resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            debouncedUpdate(entry.contentRect.width);
          }
        });

        this.resizeObserver.observe(chatContainer);
      });
    },

    setMessageActionsTimer(message) {
      if (!this.checkIfCanDeleteMessage(message)) return;

      if (this.messageActionsTimer) clearTimeout(this.messageActionsTimer);

      this.messageActionsTimer = setTimeout(() => {
        this.deleteMessage(message);
      }, 1000);
    },

    removeMessageActionsTimer() {
      if (this.messageActionsTimer) clearTimeout(this.messageActionsTimer);
    },

    checkIfChatHasScroll() {
      const chatContainer = this.$refs.chatContainer;

      return chatContainer && chatContainer.scrollHeight > chatContainer.clientHeight;
    },

    checkIfHasScroll() {
      setTimeout(() => {
        this.$nextTick(() => {
          if (!this.checkIfChatHasScroll() && this.currentPastChatId) {
            this.hasReachedStartOfCurrentConversation = true;

            this.scrollFetch(this.currentPastChatId, true);
          }
        });
      }, 1000);
    },

    checkIfCanDeleteMessage(message) {
      const passedTime = moment().diff(moment(message.sent_at), "hours");

      const fromQrCode = message.dialog.media_type == "WhatsappQrcode";

      return (message.user_id || message.bot_name) && fromQrCode && passedTime < 48;
    },

    openCarouselModal(file) {
      this.$store.dispatch("modalCarousel/setSpecificImage", file.url);

      this.$store.dispatch("modalCarousel/changeShowCarousel", true);
    },

    setAllImages() {
      const allMessagesWithImages = this.allChatData.filter(
        (message) => this.getMimeType(message.file?.mime) === "image",
      );

      const allImages = [];

      if (allMessagesWithImages.length) {
        allMessagesWithImages.forEach((element) => {
          allImages.push(element.file.url);
        });
      }

      this.$store.dispatch("modalCarousel/setCarouselImages", allImages);
    },

    stopOtherAudios(event) {
      const currentAudio = event.target;

      this.$refs["audio-player"]?.forEach((audio) => {
        if (audio !== currentAudio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    },

    isIOS() {
      const userAgent = navigator.userAgent;

      return /iPhone|iPad|iPod/i.test(userAgent);
    },

    isOpeningMessage(previousMessage, currentMessage) {
      return (
        currentMessage.dialog?.id != previousMessage?.dialog?.id ||
        (!this.hasReachedStartOfCurrentConversation && !previousMessage)
      );
    },

    handleExpiresAtAlert() {
      const alreadyExpired = this.expires_at && moment().isAfter(moment(this.expires_at));

      if (!this.expires_at || alreadyExpired) return;

      this.showExpiresAt = true;

      if (this.expiresTimout) clearTimeout(this.expiresTimout);

      this.expiresTimout = setTimeout(() => {
        this.showExpiresAt = false;
      }, 3000);
    },

    handleOppReselectData(data) {
      for (const key in data) {
        if (
          Object.prototype.hasOwnProperty.call(data, key) &&
          Object.prototype.hasOwnProperty.call(this, key)
        ) {
          this[key] = data[key];
        }
      }

      this.$nextTick(() => {
        setTimeout(() => {
          const chatContainer = this.$refs.chatContainer;

          if (chatContainer) {
            chatContainer.scrollTo({
              top: data.scrollTop,
              behavior: "smooth",
            });
          }
        }, 500);
      });
    },

    getAllDialogs() {
      if (!this.opportunity?.dialogs) {
        this.hasNoChat = true;
        return;
      }

      this.hasNoChat = false;

      const allDialogs = this.opportunity.dialogs;

      this.newestChatId = allDialogs[0].id;

      if (allDialogs.length > 1) {
        this.hasPreviousChat = true;
        this.currentPastChatId = allDialogs[1].id;

        this.currentIndexOfAllPreviousChats = 1;
      }

      if (this.oppReselectData?.newestChatId == this.newestChatId) {
        this.handleOppReselectData(this.oppReselectData);
      } else {
        this.getFirstChatData(this.newestChatId);
      }

      this.$store.commit("omnichannel/CHANGE_OPPORTUNITY_RESELECT_DATA", null);
    },

    getNextPastChat() {
      if (this.currentIndexOfAllPreviousChats + 1 >= this.opportunity.dialogs.length) {
        this.hasPreviousChat = false;

        return;
      }

      this.currentIndexOfAllPreviousChats += 1;
      this.currentPastOffset = 0;
      this.previousChatData = [];
      this.currentPastChatId = this.opportunity.dialogs[this.currentIndexOfAllPreviousChats].id;
    },

    getFormattedDate(date, showTime = false) {
      const momentDate = moment.parseZone(date);
      const formatedDate = momentDate.format("DD/MM/YYYY HH:mm");
      const yesterday = moment().subtract(1, "days");

      if (momentDate.isSame(moment(), "day")) {
        return "Hoje" + (showTime ? " - " + momentDate.format("HH:mm") : "");
      } else if (momentDate.isSame(yesterday, "day")) {
        return "Ontem" + (showTime ? " - " + momentDate.format("HH:mm") : "");
      } else {
        return formatedDate;
      }
    },

    getExpirationHours(expirationDate) {
      const now = moment();
      const expiration = moment(expirationDate);
      const totalMinutes = expiration.diff(now, "minutes");
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;
      const formattedHoursAndMinutes = `${hours}h${String(minutes).padStart(2, "0")}m`;

      if (days > 0) return `${days}d ${formattedHoursAndMinutes}`;

      return formattedHoursAndMinutes;
    },

    getExpirationStyle(expirationDate) {
      const now = moment();
      const expiration = moment(expirationDate);
      const diffHours = expiration.diff(now, "hours", true);

      if (diffHours < 3) {
        return { backgroundColor: "#ffcccc", color: "#FF004E" };
      } else if (diffHours < 8) {
        return { backgroundColor: "#fff3cd", color: "#FFA600" };
      } else {
        return { backgroundColor: "#cce5ff", color: "#046af3" };
      }
    },

    isPreviousMessageFromDifferentDay(currentMessage, previousMessage) {
      if (!previousMessage) return true;

      const currentMessageDate = moment.parseZone(currentMessage.sent_at);
      const previousMessageDate = moment.parseZone(previousMessage.sent_at);

      return !currentMessageDate.isSame(previousMessageDate, "day");
    },

    isTheOtherMessageFromSameSender(currentMessage, otherMessage) {
      if (!otherMessage) return false;

      if (currentMessage.bot_name) {
        return currentMessage.bot_name === otherMessage.bot_name;
      }

      if (currentMessage.user_id) {
        return currentMessage.user_id === otherMessage.user_id;
      }

      return !otherMessage.bot_name && !otherMessage.user_id;
    },

    getHolderStyleDependingOnSender(currentMessage, nextMessage) {
      const isSameSender = this.isTheOtherMessageFromSameSender(currentMessage, nextMessage);
      const style = { marginBottom: "12px" };

      if (currentMessage.user_id || currentMessage.bot_name) {
        style.justifyContent = "flex-end";
      }

      if (isSameSender) {
        style.marginBottom = "3px";
      }

      return style;
    },

    getMessageStyleDependingOnSender(currentMessage, nextMessage) {
      const isSameSender = this.isTheOtherMessageFromSameSender(currentMessage, nextMessage);

      const style = {};

      if (currentMessage.user_id || currentMessage.bot_name) {
        style.backgroundColor = "#dbedff";

        if (!isSameSender) {
          style.borderBottomRightRadius = "0px";
        }
      } else {
        style.backgroundColor = "#f5f6f8";

        if (!isSameSender) {
          style.borderBottomLeftRadius = "0px";
        }
      }

      if (currentMessage.error_message) {
        style.fontStyle = "italic";
        style.border = "1px solid #c01220";
        style.backgroundColor = "#f8d7da";
      }

      return style;
    },

    getMimeType(type) {
      if (!type) return null;

      const possibleTypes = {
        "audio/aac": "audio",
        "audio/mp4": "audio",
        "audio/amr": "audio",
        "audio/mpeg": "audio",
        "audio/ogg": "audio",
        "audio/mp3": "audio",
        "audio/wav": "audio",
        "audio/opus": "audio",
        "audio/webm": "audio",
        "audio/x-wav": "audio",
        "audio/vnd.wave": "audio",

        "image/jpeg": "image",
        "image/png": "image",
        "image/gif": "image",
        "image/tiff": "image",

        "image/webp": "sticker",

        "video/mp4": "video",
        "video/3gpp": "video",

        "text/plain": "document",
        "text/csv": "document",
        "text/tsv": "document",
        "text/html": "document",
        "text/rtf": "document",

        "application/pdf": "document",
        "application/msword": "document",
        "application/vnd.ms-powerpoint": "document",
        "application/vnd.ms-excel": "document",
        "application/vnd.ms-access": "document",

        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "document",
        "application/xlsx": "document",
        "application/octet-stream": "document",

        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "document",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation": "document",

        "application/vnd.oasis.opendocument.text": "document",
        "application/vnd.oasis.opendocument.spreadsheet": "document",
        "application/vnd.oasis.opendocument.presentation": "document",

        "application/zip": "document",
        "application/x-7z-compressed": "document",
        "application/x-rar-compressed": "document",
        "application/gzip": "document",
        "application/x-tar": "document",

        "application/vnd.android.package-archive": "document",
        "application/vnd.apple.pkpass": "document",
        "application/vnd.adobe.photoshop": "document",
      };

      if (!possibleTypes[type]) return "document";

      let documentType = possibleTypes[type];

      return documentType;
    },

    getReferralLabel(referral) {
      if (referral?.source_type === "post") return "Veio de uma publicação";

      return "Veio de um anúncio";
    },

    isPreviewableDocument(file) {
      if (!file || !file.mime) return false;

      if (file.mime === "application/pdf") return true;

      return this.previewableTextMimes.includes(file.mime);
    },

    pdfViewerUrl(file) {
      if (!file) return null;

      return file.url + "#toolbar=0&navpanes=0&scrollbar=0&page=1";
    },

    openDocumentInBlank(file) {
      if (!file) return;

      window.open(file.url, "_blank");
    },

    openReferralSource(referral) {
      if (!referral?.source_url) return;

      window.open(referral.source_url, "_blank", "noopener");
    },

    togglePreview(message) {
      const id = message.id;
      const status = this.documentPreviewStatus[id] || {};

      if (!status.content && !status.loading && !status.error && message.file?.mime !== "application/pdf") {
        this.loadTextPreview(message);
      }

      this.$set(this.documentPreviewStatus, id, { ...status, expanded: !status.expanded });
    },

    loadTextPreview(message) {
      const file = message.file;

      if (!this.isPreviewableDocument(file)) return;

      if (file.mime === "application/pdf") return;

      const id = message.id;
      const current = this.documentPreviewStatus[id] || {};

      if (current.loading || current.content) return;

      this.$set(this.documentPreviewStatus, id, { ...current, loading: true, error: null });

      fetch(file.url)
        .then((r) => r.text())
        .then((text) => {
          const truncated = text.length > 4000 ? text.slice(0, 4000) + "\n..." : text;
          this.$set(this.documentPreviewStatus, id, {
            loading: false,
            error: null,
            content: truncated,
            expanded: true,
          });
        })
        .catch((e) => {
          this.$set(this.documentPreviewStatus, id, {
            loading: false,
            error: e.message || "Erro ao carregar prévia",
            content: null,
            expanded: false,
          });
        });
    },

    handleMessageHighlight(div) {
      div.classList.add("hilited-message");

      setTimeout(() => {
        div.classList.remove("hilited-message");
      }, 2000);
    },

    goToMention(divId, id, manual = false) {
      if (this.isFetchingMention && manual) return;

      this.isFetchingMention = true;

      const mention = document.getElementById(divId);
      const chatContainer = this.$refs.chatContainer;

      if (mention && chatContainer) {
        const observer = new IntersectionObserver(
          (entries, observer) => {
            const entry = entries[0];

            if (entry.isIntersecting) {
              this.handleMessageHighlight(mention);

              observer.disconnect();
            }
          },
          { threshold: 0.1 },
        );

        chatContainer.scrollTo({
          top: mention.offsetTop - 100,
          behavior: "smooth",
        });

        const entry = observer.takeRecords().find((e) => e.target === mention);

        if (entry?.isIntersecting) {
          this.handleMessageHighlight(mention);
          observer.disconnect();
        } else {
          observer.observe(mention);
        }

        this.isFetchingMention = false;

        return;
      }

      this.checkScroll({ target: chatContainer }, id);
    },

    scrollToPosition(position) {
      const chatContainer = this.$refs.chatContainer;

      if (!chatContainer) return;

      chatContainer.scrollTo({ top: position, behavior: "smooth" });
    },

    checkScroll(event, target = null) {
      const element = event.target;

      const scrollingUp = element.scrollTop < this.lastScrollTop;
      this.lastScrollTop = element.scrollTop;

      if (scrollingUp) this.handleExpiresAtAlert();

      const scrollTop = Math.abs(element.scrollTop);
      const maxScrollTop = Math.abs(element.scrollHeight - element.clientHeight);

      const tolerance = 2;

      if (Math.abs(scrollTop - maxScrollTop) <= tolerance || target) {
        if (this.firstTotalCount > this.chatData.length) {
          this.scrollFetch(this.newestChatId, false, target);

          this.hasReachedStartOfCurrentConversation = false;

          return;
        }

        this.hasReachedStartOfCurrentConversation = true;

        if (this.hasPreviousChat && this.currentTotalCount >= this.previousChatData.length) {
          this.scrollFetch(this.currentPastChatId, true, target);
        }
      }
    },

    handleScrollData(response, fromPreviousChats = false) {
      const oldMessages = response.data;

      if (fromPreviousChats) {
        this.currentPastOffset += oldMessages.length;
        this.currentTotalCount = response.pagination.total_count;

        this.allPreviousChatData = [...this.allPreviousChatData, ...oldMessages];
        this.previousChatData = [...this.previousChatData, ...oldMessages];

        if (this.previousChatData.length >= this.currentTotalCount) {
          this.getNextPastChat();

          if (!this.checkIfChatHasScroll() && this.hasPreviousChat && !this.loopBreak) {
            this.scrollFetch(this.currentPastChatId, true);
          }
        }

        this.setAllImages();

        return;
      }

      const filteredArray = oldMessages.filter((obj1) => {
        return !this.chatData.some((obj2) => {
          return obj1.id === obj2.id;
        });
      });

      this.newestOffset += filteredArray.length;
      this.firstTotalCount = response.pagination.total_count;

      this.chatData = [...this.chatData, ...filteredArray];

      this.setAllImages();
    },

    handlePullingData(response) {
      const newMessages = response.data;

      const filteredArray = newMessages.filter((obj1) => {
        return !this.chatData.some((obj2) => {
          return obj1.id === obj2.id;
        });
      });

      this.newestOffset += filteredArray.length;
      this.firstTotalCount = response.pagination.total_count;

      this.chatData = [...filteredArray, ...this.chatData];

      if (newMessages?.length) {
        newMessages.forEach((newElement) => {
          this.chatData.forEach((element, i) => {
            if (element.id == newElement.id) {
              let updatedElement = { ...element, ...newElement };

              this.$set(this.chatData, i, updatedElement);
            }
          });
        });
      }

      this.setAllImages();
    },

    applyMessageReaction(data) {
      if (!data) return;

      [this.chatData, this.allPreviousChatData].forEach((messages) => {
        const index = messages.findIndex(
          (message) =>
            message.id == data.dialog_message_id ||
            (Boolean(data.remote_id) && message.remote_id === data.remote_id),
        );

        if (index === -1) return;

        this.$set(messages, index, {
          ...messages[index],
          reaction_emoji: data.reaction_emoji || null,
        });
      });
    },

    handleRedirectFromRedirect(data) {
      if (!this.fromOmniRedirect) {
        this.handleOppReselectData(this.oppReselectData);

        if (data.id == this.opportunity.id) {
          const position = this.oppReselectData?.scrollTop;

          this.scrollToPosition(position);

          this.loopBreak = false;

          this.startPullingLoop();

          return;
        }

        this.opportunityFromRedirect = data;

        this.$nextTick(() => {
          this.$bvModal.show("bv-opportunity-from-chat-redirect");
        });
      }
    },

    getFirstChatData(id) {
      this.hasChatLoading = true;

      const params = [
        { query: "limit", param: this.limit },
        { query: "offset", param: this.newestOffset },
      ];

      const data = {
        chat_id: id,

        params: this.$service.formatQueryParam(params),
      };

      this.$store
        .dispatch("omnichannel/getChat", data)
        .then((response) => {
          this.chatData = response.data;

          this.newestOffset += this.chatData.length;
          this.firstTotalCount = response.pagination.total_count;

          if (this.chatData.length >= this.firstTotalCount) {
            this.hasReachedStartOfCurrentConversation = true;
          }

          this.startPullingLoop();
          this.setAllImages();
          this.checkIfHasScroll();
          this.handleExpiresAtAlert();
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.hasChatLoading = false;
        });
    },

    scrollFetch(id, isFromPreviousChats = false, target = null) {
      if (this.hasScrollLoading) return;

      this.hasScrollLoading = true;

      const params = [
        { query: "limit", param: this.limit },

        {
          query: "offset",
          param: isFromPreviousChats ? this.currentPastOffset : this.newestOffset,
        },

        { query: "workflow_id", param: this.$service.getWorkflowId() },
      ];

      const data = {
        chat_id: id,

        params: this.$service.formatQueryParam(params),
      };

      this.$store
        .dispatch("omnichannel/getChat", data)
        .then((response) => {
          this.hasScrollLoading = false;

          this.handleScrollData(response, isFromPreviousChats);

          if (target) {
            const targetMessage = response.data.find((message) => message.id == target);

            if (targetMessage) {
              this.$nextTick(() => {
                this.goToMention("message-card-" + target, target);
              });

              return;
            }

            this.checkScroll({ target: this.$refs.chatContainer }, target);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.hasScrollLoading = false;
        });
    },

    pullingFetch(id) {
      if (this.hasPullingLoading || this.hasScrollLoading) return;

      this.hasPullingLoading = true;
      this.hasNewMessage = false;

      const params = [
        { query: "limit", param: this.limit },
        { query: "offset", param: 0 },
        { query: "workflow_id", param: this.$service.getWorkflowId() },
      ];

      const data = {
        chat_id: id,

        params: this.$service.formatQueryParam(params),
      };

      this.$store
        .dispatch("omnichannel/getChat", data)
        .then((response) => {
          if (response.data.length) {
            this.handlePullingData(response);
            this.$emit("update-dialog", response.data[0].dialog);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.hasPullingLoading = false;
        });
    },

    openOpportunity(message) {
      if (this.hasOpReselectRequest) return;

      this.hasOpReselectRequest = true;

      const opp_id = message?.dialog?.opportunity?.id;

      if (opp_id == this.opportunity.id) {
        this.hasOpReselectRequest = false;

        return;
      }

      if (opp_id) {
        this.$store
          .dispatch("opportunityStore/fetchOne", opp_id)
          .then((response) => {
            this.opportunityFromRedirect = response.data;

            const scrollContainer = this.$refs.chatContainer;

            const emitData = {
              newestChatId: this.newestChatId,
              currentPastChatId: this.currentPastChatId,

              chatData: this.chatData,
              previousChatData: this.previousChatData,
              allPreviousChatData: this.allPreviousChatData,

              newestOffset: this.newestOffset,
              currentPastOffset: this.currentPastOffset,

              firstTotalCount: this.firstTotalCount,
              currentTotalCount: this.currentTotalCount,
              currentIndexOfAllPreviousChats: this.currentIndexOfAllPreviousChats,

              hasNoChat: this.hasNoChat,
              hasPreviousChat: this.hasPreviousChat,
              hasReachedStartOfCurrentConversation: this.hasReachedStartOfCurrentConversation,

              scrollTop: scrollContainer?.scrollTop,
            };

            this.$store.commit("omnichannel/CHANGE_OPPORTUNITY_RESELECT_DATA", emitData);

            if (this.fromOmnichannelDesktop) {
              this.$emit("selectOpFromOmnichat", response.data);

              return;
            }

            this.loopBreak = true;
            this.stopPullingLoop();

            if (this.fromOmniRedirect) {
              this.$bvModal.hide("bv-opportunity-from-chat-redirect");

              this.$nextTick(() => {
                this.$emit("opportunityRedirect", response.data);
              });

              return;
            }

            this.$bvModal.show("bv-opportunity-from-chat-redirect");
          })
          .finally(() => {
            this.hasOpReselectRequest = false;
          });
      }
    },

    deleteMessage(message) {
      if (this.hasDeletionRequest) return;

      const sure = confirm("Você deseja excluir essa mensagem?");

      if (sure) {
        this.hasDeletionRequest = true;

        const deletionData = {
          message_id: message.remote_id,
          dialog_id: message.dialog_id,
        };

        this.$store
          .dispatch("omnichannel/deleteMessage", deletionData)
          .then(() => {
            this.$bvToast.toast("Mensagem excluída com sucesso!", {
              title: "Sucesso",
              variant: "success",
              solid: true,
            });
          })
          .finally(() => {
            this.hasDeletionRequest = false;
          });
      }
    },

    resendMessage(message) {
      if (this.reengagementRequired) return;

      if (this.sendingMessageId) {
        this.$bvToast.toast("Aguarde o reenvio de uma mensagem antes de tentar reenviar outra!", {
          title: "Reenvio em andamento",
          variant: "warning",
          solid: true,
        });

        return;
      }

      this.sendingMessageId = message.id;

      const newMessage = message.message;
      const now = moment().format("YYYY-MM-DDTHH:mm:ss.SSSZ");

      const bodyObject = {
        dialog_id: this.opportunity.dialogs[0].id,

        message_object: {
          message: newMessage || null,
          timestamp: now,
        },
      };

      if (message.file) {
        this.sendMessageWithFile(message.file, bodyObject);

        return;
      }

      this.$store.dispatch("omnichannel/sendMessage", bodyObject).finally(() => {
        this.sendingMessageId = null;
      });
    },

    async sendMessageWithFile(file, message) {
      const reader = new FileReader();
      const mimeType = file.mime;

      const filePromise = new Promise((resolve) => {
        reader.onload = (event) => {
          const base64String = event.target.result.split(",")[1];

          const fileData = {
            name: file.name,
            base64File: "data:" + mimeType + ";base64," + base64String,
          };

          resolve(fileData);
        };

        reader.readAsDataURL(file);
      });

      const fileData = await filePromise;

      const messageObject = { ...message };

      messageObject.dialogId = this.opportunity.remote_id;
      messageObject.message_object.message = null;
      messageObject.message_object.file = fileData;

      this.$store.dispatch("omnichannel/sendMessage", messageObject).finally(() => {
        this.sendingMessageId = null;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-spinner-holder {
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;

  .absolute-loading {
    position: absolute;
    top: 20px;
    left: calc(50% - 18px);
    z-index: 2000;
  }

  .no-chat-image {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;

    img {
      width: 100%;
    }
  }

  .modalPadding {
    padding-bottom: 63px;
  }

  .chat-container {
    display: flex;
    flex-direction: column-reverse;
    height: 100%;
    width: 100%;
    gap: 3px;
    padding: 10px 15px 0 15px;
    overflow-y: scroll;

    .expiration-wrapper {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10;
      overflow: hidden;
      border-radius: 0 0 16px 16px;
    }

    .slide-down-enter-active,
    .slide-down-leave-active {
      transition: transform 0.3s ease;
    }

    .slide-down-enter,
    .slide-down-leave-to {
      transform: translateY(-100%);
      border-radius: 0 0 16px 16px;
    }

    .slide-down-enter-to,
    .slide-down-leave {
      transform: translateY(0);
      border-radius: 0 0 16px 16px;
    }

    .expiration-info {
      display: flex;
      align-items: center;
      font-weight: 600;
      padding: 10px 15px;
      gap: 5px;
      white-space: nowrap;
    }

    .message-divider-holder {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 5px;

      .line-and-chat-start {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 1px;
        width: 100%;
        margin: 25px 0;
        background: #e5e5e5;

        .start-message {
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 10px;
          max-width: 90%;
          padding: 4px 15px;
          border-radius: 800px;
          background: #c4e2ff;
          color: #192a3e;
        }
      }

      .message-holder {
        display: flex;
        align-items: center;
        width: 100%;
        padding: 0 10px;
        gap: 5px;

        .deleted-message {
          display: flex;
          justify-content: center;
          align-items: center;
          font-style: italic;
          text-align: left;
          max-width: 500px;
          gap: 5px;
          padding: 10px;
          border-radius: 16px 16px 0px 16px;
          color: #969696;
          overflow: hidden;
          background: #dbedff;
        }

        .audio-holder {
          position: relative;

          &.has-reaction {
            padding-bottom: 24px;
          }

          .audio-reaction {
            position: absolute;
            bottom: 2px;
            font-size: 13px;
            line-height: 1;
            padding: 3px 6px;
            border-radius: 800px;
            box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.15);
            background: #ffffff;
          }

          .desktop-message-actions {
            right: 10px;
            background: #f1f3f4;
          }

          .desktop-message-actions-native {
            visibility: hidden;
            position: absolute;
            top: -10px;
            right: -5px;
            padding: 5px;
            border-radius: 16px;
            box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.1);
            background: #f1f3f4;
            cursor: pointer;

            @media (max-width: 800px) {
              display: none;
            }
          }

          .absolute-time-check {
            position: absolute;
          }
        }

        .desktop-message-actions {
          visibility: hidden;
          position: absolute;
          top: 5px;
          padding: 5px;
          border-radius: 16px;
          box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.1);
          cursor: pointer;

          @media (max-width: 800px) {
            display: none;
          }
        }

        .message-card {
          display: flex;
          flex-direction: column;
          position: relative;
          text-align: left;
          max-width: 500px;
          gap: 5px;
          padding: 10px;
          border-radius: 16px;
          color: #000000;
          overflow: hidden;

          .desktop-message-actions {
            right: 5px;
            background: #dbedff;
          }

          .referral-card {
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: 5px;
            padding: 8px;
            border-radius: 6px;
            border-left: 4px solid #ffa600;
            background: rgba(255, 255, 255, 0.65);
            overflow: hidden;

            &.referral-clickable {
              cursor: pointer;
            }

            .referral-label {
              display: flex;
              align-items: center;
              font-size: 11px;
              font-weight: 600;
              gap: 5px;
              color: #ffa600;
            }

            .referral-thumbnail {
              width: 100%;
              max-height: 140px;
              border-radius: 6px;
              object-fit: cover;
            }

            .referral-headline {
              font-size: 13px;
              font-weight: 600;
              word-break: break-word;
              color: #0f1828;
            }

            .referral-body {
              display: -webkit-box;
              font-size: 12px;
              word-break: break-word;
              color: #6c757d;
              overflow: hidden;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 3;
              line-clamp: 3;
            }

            .referral-link {
              display: flex;
              align-items: center;
              font-size: 11px;
              font-weight: 600;
              gap: 4px;
              color: #046af3;
            }
          }

          .message-reaction {
            display: flex;
            align-items: center;
            align-self: flex-start;
            font-size: 13px;
            line-height: 1;
            padding: 3px 6px;
            border-radius: 800px;
            box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.15);
            background: #ffffff;
          }

          .response-message {
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 5px;
            border-radius: 6px;
            border-left: 4px solid #c4e2ff;
            overflow: hidden;
            cursor: pointer;

            .midia-response {
              display: -webkit-box;
              align-items: flex-start;
              word-break: break-word;
              text-overflow: ellipsis;
              width: 100%;
              height: 100%;
              max-height: 120px;
              gap: 5px;
              color: #adb5bd;
              overflow: hidden;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              line-clamp: 2;
            }
          }

          .message-header {
            font-size: 15px;
            font-weight: bold;
            overflow-wrap: break-word;
            width: 100%;
            color: #000000;
          }

          .file-holder {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            min-width: 200px;
            margin-bottom: 10px;
            border-radius: 10px;
            overflow: hidden;

            .visual-file {
              width: 100%;
              border-radius: 10px;
              cursor: pointer;
            }

            .document-file {
              display: flex;
              align-items: center;
              justify-content: space-between;
              width: 100%;
              max-width: 350px;
              padding: 5px;
              border-radius: 16px 16px 16px 0px;
              background: #f0eff5;
              color: #0f1828;
              cursor: pointer;

              .file-name {
                line-height: 16px;
                letter-spacing: 0em;
                white-space: nowrap;
                text-overflow: ellipsis;
                height: 100%;
                padding: 1px 0;
                max-width: 80%;
                overflow: hidden;
              }

              i {
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 20px;
                height: 40px;
                width: 40px;
                border-radius: 50%;
                background: #f0eff5;
                color: #0f1828;
              }
            }

            .document-preview-wrapper {
              display: flex;
              flex-direction: column;
              width: 100%;
              padding: 5px;
              gap: 100px;
              color: #0f1828;

              .pdf-preview-card {
                display: flex;
                flex-direction: column;
                width: 100%;
                gap: 15px;

                .pdf-object {
                  min-height: 260px;
                  width: 100%;
                  background: #ffffff;
                }

                .doc-meta-row {
                  display: flex;
                  align-items: center;
                  gap: 6px;

                  .file-name {
                    flex: 1;
                    font-size: 12px;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                  }
                }
              }

              .text-preview-card {
                display: flex;
                flex-direction: column;
                gap: 15px;

                .text-preview-header {
                  display: flex;
                  align-items: center;
                  gap: 6px;

                  .file-name {
                    flex: 1;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-size: 12px;
                    overflow: hidden;
                  }
                }

                .text-preview-body {
                  max-height: 260px;
                  border-radius: 8px;
                  border: 1px solid #e2e6ea;
                  background: #ffffff;

                  .text-content {
                    white-space: pre-wrap;
                    word-break: break-word;
                    font-size: 12px;
                    padding: 10px;
                    margin: 0;
                  }

                  .preview-error {
                    font-size: 12px;
                    color: #c01220;
                  }

                  .preview-hint {
                    font-size: 12px;
                    color: #6c757d;
                  }
                }
              }

              .preview-action {
                font-size: 20px;

                cursor: pointer;

                &:hover {
                  color: #046af3;
                }
              }
            }
          }

          .message-footer {
            font-size: 13px;
            color: rgba(0, 0, 0, 0.45);
          }
        }

        .message-error-holder {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;

          .resend-holder {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
            cursor: pointer;

            .resend-icon {
              font-size: 25px;
              color: #6c757d;

              &:hover {
                color: #046af3;
              }
            }
          }
        }

        .bot-avatar {
          height: 28px;
          width: 28px;
        }

        .file-error {
          border: 5px solid #c01220 !important;
        }

        &:hover {
          .desktop-message-actions,
          .desktop-message-actions-native {
            visibility: visible;
          }
        }
      }
    }

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-track {
      border-radius: 13px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 13px;
      border: 1px solid transparent;
      background-clip: content-box;
      box-shadow: inset 0 0 0 5px #c8c8c8;
    }

    .invisible {
      opacity: 0;
    }
  }

  .hilited-message {
    border-radius: 16px;
    animation: hilited-message-animation 1.5s ease-in-out 1;
  }

  @keyframes hilited-message-animation {
    0% {
      box-shadow: 0 0 0 2px #c4e2ff;
    }

    50% {
      box-shadow: 0 0 0 2px #046af3;
    }

    100% {
      box-shadow: 0 0 0 2px #c4e2ff;
    }
  }

  .avatar-container {
    margin: 0 !important;
  }
}
</style>
